import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Divider,
} from '@heroui/react';
import { Icon } from '@iconify/react';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { EduModal } from '@/components/education/EduModal';
import useAppEvents from '@/patient-app-common/hooks/useAppEvents';
import { AppEvent } from '@/patient-app-common/types/ApplicationEvent';

export function TodoListReadOnly() {
  const { activeTodos } = useAppEvents();

  return (
    <>
      {!_.isEmpty(activeTodos) && (
        <Card shadow="sm">
          <CardBody className="p-0">
            {activeTodos
              .sort((a, b) => Number(a.done) - Number(b.done))
              .map((event) => (
                <TodoEvent key={event.title} event={event} />
              ))}
          </CardBody>
        </Card>
      )}
    </>
  );
}

function TodoEvent({ event }: { event: AppEvent }) {
  const { t } = useTranslation('todo');
  const navigate = useNavigate();

  const [eduModal, toggleEduModal] = useState(false);

  const formatSubtitle = (initText: string) => {
    const today = t('common.text.today');
    let formattedText = today;
    if (initText.length > 0) {
      const subtitle = initText.charAt(0).toLowerCase() + initText.slice(1);
      formattedText = `${today}, ${subtitle}`;
    }
    return formattedText;
  };

  const subtitle = useMemo(() => {
    if (!event.repeating || !event.subtitle) return event.subtitle;
    return formatSubtitle(event.subtitle);
  }, [event]);

  return (
    <>
      <EduModal
        event={event}
        source="TodoEvent"
        onClose={() => toggleEduModal(false)}
        isOpen={eduModal}
      />
      <Card className="px-4 w-full" radius="none" shadow="none">
        <CardHeader className="flex justify-end px-0 py-1">
          {event.repeating && (
            <Icon
              aria-hidden="true"
              className="text-success-500 text-xl"
              icon="mdi:repeat"
            />
          )}
        </CardHeader>
        <CardBody className="pt-0">
          <li className={event.done ? 'line-through' : ''}>{event.title}</li>
          <p className={`font-light pl-6 ${event.done ? 'line-through' : ''}`}>
            {subtitle}
          </p>
          {!_.isEmpty(event.profileDependencyNotes) && (
            <p
              className={`italic font-light pl-6 ${event.done ? 'line-through' : ''} whitespace-pre-line`}
            >
              {t('todolistreadonly.profile_dependency_note')}{' '}
              {event.profileDependencyNotes}
            </p>
          )}
          {event.done && event.completedDate && (
            <p className="font-light text-sm pl-6">
              {t('common.text.completed_date', {
                date: dayjs(event.completedDate).format('LLLL'),
              })}
            </p>
          )}
        </CardBody>
        <CardFooter className="flex flex-col items-end">
          {!_.isEmpty(event.edIds) && (
            <Button
              color="primary"
              onPress={() => toggleEduModal(true)}
              variant="flat"
            >
              {event.options.linkText || t('common.link.learn_more')}
            </Button>
          )}
          {!_.isEmpty(event.options.id) && (
            <Button
              color="primary"
              onPress={() =>
                navigate(`/tracker/${event.options.id}`, {
                  state: {
                    eventId: event.id,
                  },
                })
              }
              variant="flat"
            >
              {event.options.linkText || t('common.link.add_entry')}
            </Button>
          )}
        </CardFooter>
      </Card>
      <Divider />
    </>
  );
}
